// What the Client Reports filters mean, in one place, so the screen and the CSV export
// can't drift apart. Pure functions only; pinned by scripts/test-report-filters.mjs.
//
// The filters (the row of dropdowns above the report), all optional:
//   clientId    one client
//   taskTypeId  one task type
//   tagId       one tag
//   model       a billing model ('hourly' | 'retainer' | 'hybrid'; 'all' or '' = any)
//   activity    'active' (clients with time in the period) | 'inactive' (clients with none) | 'all'
// plus the date range, which has already been applied to `entries` by the caller.
//
// Two levels, because the Summary lists clients and the Detailed view and the CSV list entries:
//   entry level   an entry matches when it is the chosen client's, task type's and tag's
//   client level  a client row stays when its billing model and activity match
// filterReportEntries applies both, so exporting a report gives exactly the time entries behind
// the rows you are looking at.

const modelOn = (f) => !!f && !!f.model && f.model !== 'all';
const activityOn = (f) => !!f && !!f.activity && f.activity !== 'all';

// Does this one time entry match the filters? `clientById` maps id -> client.
export function entryPassesFilters(e, f, clientById) {
  if (!f) return true;
  if (f.clientId && e.client_id !== f.clientId) return false;
  if (f.taskTypeId && e.task_type_id !== f.taskTypeId) return false;
  if (f.tagId && !(e.time_entry_tags || []).some(t => t.tag_id === f.tagId)) return false;
  if (modelOn(f)) {
    const cl = clientById ? clientById[e.client_id] : null;
    if (!cl || cl.model !== f.model) return false; // an entry with no client has no billing model
  }
  return true;
}

// The time entries a report is showing. With no filters this is every entry in `entries`,
// including internal-client and no-client ones, as an unfiltered export always has been.
// "No activity" asks for clients that have NO time in the period, so there are no entries to list.
export function filterReportEntries(entries, f, clients) {
  if (activityOn(f) && f.activity === 'inactive') return [];
  const clientById = Object.fromEntries((clients || []).map(c => [c.id, c]));
  return (entries || []).filter(e => entryPassesFilters(e, f, clientById));
}

// Client-row level: the filters that decide whether a client appears at all. `ces` is that
// client's entries in the period after the entry-level filters.
export function clientLevelPasses(cl, ces, f) {
  if (!f) return true;
  if (f.clientId && cl.id !== f.clientId) return false;
  if (modelOn(f) && cl.model !== f.model) return false;
  if (f.activity === 'active' && (ces || []).length === 0) return false;
  if (f.activity === 'inactive' && (ces || []).length > 0) return false;
  return true;
}

// A short, file-name-safe description of the active filters, so an exported file says what
// it contains: "Acme_Corp", "Acme_Corp_Meeting", "hourly" ... Empty when nothing is filtered.
export function filterFileSuffix(f, { clients = [], taskTypes = [], tags = [], modelLabel = (m) => m } = {}) {
  if (!f) return '';
  const parts = [];
  if (f.clientId) parts.push(clients.find(c => c.id === f.clientId)?.name);
  if (f.taskTypeId) parts.push(taskTypes.find(t => t.id === f.taskTypeId)?.name);
  if (f.tagId) parts.push(tags.find(t => t.id === f.tagId)?.name);
  if (modelOn(f)) parts.push(modelLabel(f.model));
  if (f.activity === 'active') parts.push('with_activity');
  return parts.filter(Boolean).join('_').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
}
